import { supabase } from '../../../services/supabase/client'
import {
  REPORT_PHOTO_BUDGET_BYTES,
  REPORT_PHOTO_MAX_PX,
  resolveImagesToDataUrls,
} from './pdfImageService'
import type { PdfImageTask } from './pdfImageService'
import { generateSiteMap } from './staticMapService'
import { parseSiteMapLayout } from './siteMap/siteMapGeometry'
import type { MapBookRender } from './siteMap/types'
import {
  applyPurposeToToggles,
  buildReportBrand,
  effectiveLayout,
  resolveBrandPreset,
} from './reportBrandService'
import { parseSiteBoundary } from '../../jobs/types/siteBoundary'
import { customNodeTag } from '../../jobs/utils/nodeTag'
import { DEFAULT_LAYOUT, parseBrandLayout } from '../../branding/types'
import type { BrandLayout, BrandPreset } from '../../branding/types'
import { effectiveSectionOrder } from '../config/sectionOrder'
import type { ReportType } from '../config/reportType'
import type {
  ReportDocumentProps,
  ReportNode,
  ReportPhoto,
  ReportArboristProfile,
  ReportDistributionEntry,
  ReportRevisionEntry,
  ReportSections,
  ReportToggles,
} from '../components/ReportDocument'

export interface LoadedBrand {
  preset: BrandPreset | null
  layout: BrandLayout | null
  logoSignedUrl: string | null
  logoLightSignedUrl: string | null
}

/**
 * Resolves the brand a job's document renders with (draft choice → client
 * default → org default → none) and signs its logo assets. Shared by
 * reports and quotes (008 R8) so both resolve the brand identically; every
 * step is best-effort and degrades to unbranded rather than failing.
 */
export async function loadBrandForJob(opts: {
  reportData: Record<string, unknown> | null | undefined
  clientPresetId: string | null
  orgId: string | null
}): Promise<LoadedBrand> {
  const preset = await resolveBrandPreset(opts)
  const layout = preset ? effectiveLayout(preset, opts.reportData) : null

  const signBrandAsset = async (path: string | null) => {
    if (!path) return null
    try {
      const { data } = await supabase.storage.from('brand-assets').createSignedUrl(path, 3600)
      return data?.signedUrl ?? null
    } catch {
      return null
    }
  }
  const [logoSignedUrl, logoLightSignedUrl] = preset
    ? await Promise.all([signBrandAsset(preset.logo_path), signBrandAsset(preset.logo_light_path)])
    : [null, null]

  return { preset, layout, logoSignedUrl, logoLightSignedUrl }
}

/**
 * Image tasks for the brand logos. They keep their transparency (PNG, no
 * background flatten) so the cover can place them on the primary panel or a
 * knockout tile, and they are OPTIONAL: a logo that fails to sign or fetch
 * (deleted file, flaky connection) must never block generation — the cover
 * falls back to the knockout tile / org name instead.
 */
export function brandImageTasks(
  preset: BrandPreset | null,
  logoSignedUrl: string | null,
  logoLightSignedUrl: string | null
): PdfImageTask[] {
  const tasks: PdfImageTask[] = []
  if (preset?.logo_path) {
    tasks.push({
      key: 'brand_logo',
      label: 'Brand logo',
      signedUrl: logoSignedUrl,
      format: 'png',
      optional: true,
    })
  }
  if (preset?.logo_light_path) {
    tasks.push({
      key: 'brand_logo_light',
      label: 'Brand logo (light)',
      signedUrl: logoLightSignedUrl,
      format: 'png',
      optional: true,
    })
  }
  return tasks
}

/**
 * Assembles all data needed to render a client-side PDF report.
 *
 * All images (photos, signature, site map, TPZ diagram) are materialised as
 * data URLs before this function returns (RPT-02): signed URLs are fetched
 * with retry + bounded concurrency, downscaled and re-encoded, with the
 * offline IndexedDB photo store consulted first. Once this resolves, PDF
 * rendering needs no network at all — killing the connection after this
 * point still yields a complete document.
 */
export async function assemblePdfData(
  jobId: string,
  version: number,
  reportSummary?: string | null,
  reportOverallRecommendations?: string | null,
  reportData?: Record<string, unknown> | null,
  onImageProgress?: (done: number, total: number) => void,
  opts?: { variant?: string | null }
): Promise<ReportDocumentProps> {
  // Variant (008 R7): the report row's type, defaulting to the full report
  const variant: ReportType =
    opts?.variant === 'findings_report' ? 'findings_report' : 'full_report'
  const rdEarly = reportData ?? {}
  const wantsPhotos =
    variant === 'full_report' ||
    (typeof rdEarly['include_tree_photos'] === 'boolean' ? rdEarly['include_tree_photos'] : false)
  // -------------------------------------------------------------------------
  // 1. Job with client organisation and arborist
  // -------------------------------------------------------------------------
  const { data: job, error: jobError } = await supabase
    .from('jobs')
    .select(
      `
      id,
      title,
      job_number,
      description,
      scheduled_date,
      site_address_line1,
      site_address_city,
      site_address_state,
      site_address_postal_code,
      site_boundary,
      status,
      org_id,
      arborist_id,
      client_organizations!jobs_client_org_id_fkey ( id, name, contact_name, default_brand_preset_id )
    `
    )
    .eq('id', jobId)
    .single()

  if (jobError || !job) {
    throw new Error(`Failed to fetch job data: ${jobError?.message ?? 'not found'}`)
  }

  // Supabase returns joined rows as objects (or null when there's no match)
  const clientOrg = job.client_organizations as {
    id: string
    name: string
    contact_name: string | null
    default_brand_preset_id: string | null
  } | null
  // Two people: the job's arborist ("Inspected by", Job Information) and the
  // assessor — whoever generates the report, who signs it with their own
  // profile. A teammate cannot sign with someone else's signature, and their
  // profile is not readable anyway (arborist_profiles RLS is own-row-only).
  // The session is local, so this works offline; with no session the job's
  // arborist signs, as before.
  const {
    data: { session },
  } = await supabase.auth.getSession()
  const assessorId = session?.user.id ?? job.arborist_id
  // Not embedded from users: its RLS is own-row-only, so a teammate's name
  // came back null. team_member_names returns names alone, for teammates and
  // past authors on the team's jobs.
  const names = await fetchUserNames([job.arborist_id, assessorId].filter(Boolean) as string[])
  const withName = (id: string | null) =>
    id ? { id, first_name: null, last_name: null, ...names.get(id) } : null
  const arboristUser = withName(job.arborist_id)
  const assessorUser = withName(assessorId)

  if (!clientOrg) {
    throw new Error('Job has no associated client organisation')
  }

  // -------------------------------------------------------------------------
  // 2. Assessor's profile (best-effort — not all arborists have one)
  // -------------------------------------------------------------------------
  let profileRow: {
    company_name: string | null
    abn: string | null
    phone: string | null
    qualifications: string[] | null
    certifications: string[] | null
    memberships: string[] | null
    licence_numbers: string[] | null
    signature_image_path: string | null
  } | null = null

  if (assessorUser?.id) {
    const { data: profile } = await supabase
      .from('arborist_profiles')
      .select(
        'company_name, abn, phone, qualifications, certifications, memberships, licence_numbers, signature_image_path'
      )
      .eq('id', assessorUser.id)
      .maybeSingle()
    profileRow = profile ?? null
  }

  // -------------------------------------------------------------------------
  // 2b. Brand preset (003): report choice → client default → org default →
  //     none. Best-effort — offline resolution failures degrade to unbranded.
  //     Shared with quotes (008) so both document types resolve identically.
  // -------------------------------------------------------------------------
  const {
    preset: brandPreset,
    layout: brandLayout,
    logoSignedUrl,
    logoLightSignedUrl,
  } = await loadBrandForJob({
    reportData,
    clientPresetId: clientOrg.default_brand_preset_id,
    orgId: (job as { org_id?: string | null }).org_id ?? null,
  })

  // -------------------------------------------------------------------------
  // 3. Inspection nodes for this job
  // -------------------------------------------------------------------------
  const { data: rawNodes, error: nodesError } = await supabase
    .from('inspection_nodes')
    .select('*')
    .eq('job_id', jobId)
    .order('node_number', { ascending: true })

  if (nodesError) {
    throw new Error(`Failed to fetch trees: ${nodesError.message}`)
  }

  const nodeList = rawNodes ?? []

  // -------------------------------------------------------------------------
  // 4. Photos for all nodes in one query, signed URLs in one batch call
  // -------------------------------------------------------------------------
  const nodeIds = nodeList.map((n) => n.id)
  type PhotoRow = {
    id: string
    node_id: string | null
    storage_path: string
    caption: string | null
  }
  let photoRows: PhotoRow[] = []

  // Findings reports fetch photos only when the schedule thumbnails are on
  if (nodeIds.length > 0 && wantsPhotos) {
    const { data: photos, error: photosError } = await supabase
      .from('photos')
      .select('id, node_id, storage_path, caption')
      .in('node_id', nodeIds)
      .order('created_at', { ascending: true })

    if (photosError) {
      throw new Error(`Failed to fetch photos: ${photosError.message}`)
    }
    photoRows = photos ?? []
  }

  const signedUrlByPath = new Map<string, string>()
  if (photoRows.length > 0) {
    const { data: signedList, error: signError } = await supabase.storage
      .from('photos')
      .createSignedUrls(
        photoRows.map((p) => p.storage_path),
        3600
      )
    if (signError) {
      throw new Error(`Failed to sign photo URLs: ${signError.message}`)
    }
    for (const entry of signedList ?? []) {
      if (entry.signedUrl && entry.path) signedUrlByPath.set(entry.path, entry.signedUrl)
    }
  }

  // -------------------------------------------------------------------------
  // 5. Report asset images (signature, site map, TPZ diagram) — signed URLs
  // -------------------------------------------------------------------------
  const siteMapPath =
    typeof reportData?.site_map_path === 'string' && reportData.site_map_path
      ? reportData.site_map_path
      : null
  const tpzDiagramPath =
    typeof reportData?.tpz_diagram_path === 'string' && reportData.tpz_diagram_path
      ? reportData.tpz_diagram_path
      : null
  const signaturePath = profileRow?.signature_image_path ?? null

  const signAsset = async (path: string | null) => {
    if (!path) return null
    const { data } = await supabase.storage.from('report-assets').createSignedUrl(path, 3600)
    return data?.signedUrl ?? null
  }

  const [signatureSignedUrl, siteMapSignedUrl, tpzDiagramSignedUrl] = await Promise.all([
    signAsset(signaturePath),
    signAsset(siteMapPath),
    signAsset(tpzDiagramPath),
  ])

  // -------------------------------------------------------------------------
  // 5b. Auto-generated site map with labelled tree markers. A manually
  //     uploaded site map image overrides it; tile fetch failures (offline)
  //     degrade to no map rather than failing generation.
  // -------------------------------------------------------------------------
  //     009: the per-report layout setting (Automatic / Single map / Map
  //     book) decides between one map and an overview plus detail sheets.
  // -------------------------------------------------------------------------
  let autoSiteMapDataUrl: string | null = null
  let mapBook: MapBookRender | null = null
  if (!siteMapPath) {
    const trees = nodeList
      .filter((n) => n.gps_latitude != null && n.gps_longitude != null)
      .map((n) => ({
        nodeNumber: n.node_number,
        latitude: n.gps_latitude,
        longitude: n.gps_longitude,
        // Bare number: the badge is the "node" cue, and the prefix ate the room
        label: customNodeTag(n) ?? String(n.node_number),
      }))
    // Site boundary widens the framing and is drawn as an overlay
    const boundary = parseSiteBoundary(job.site_boundary)
    if (trees.length > 0 || boundary) {
      try {
        const rendered = await generateSiteMap({
          trees,
          boundary,
          layout: parseSiteMapLayout(reportData?.site_map_layout),
          // Unbranded = FALLBACK_LAYOUT's density (chrome.tsx pulls in react-pdf)
          density: brandLayout?.density ?? 'standard',
        })
        if (rendered?.kind === 'single') autoSiteMapDataUrl = rendered.imageUrl
        if (rendered?.kind === 'book') {
          const { kind: _kind, ...book } = rendered
          mapBook = book
        }
      } catch {
        autoSiteMapDataUrl = null
        mapBook = null
      }
    }
  }

  // -------------------------------------------------------------------------
  // 6. Materialise every image before rendering (retry, concurrency 4,
  //    offline-first, downscale, JPEG 0.8) — see pdfImageService. Photos are
  //    sized for print, kept as Buffers (never Blobs) and share a byte budget:
  //    an 854-photo report otherwise outgrew the 2 GB iOS gives Safari's page
  // -------------------------------------------------------------------------
  const photosByNode = new Map<string, PhotoRow[]>()
  for (const photo of photoRows) {
    if (!photo.node_id) continue
    const list = photosByNode.get(photo.node_id) ?? []
    list.push(photo)
    photosByNode.set(photo.node_id, list)
  }

  const imageTasks: PdfImageTask[] = []
  for (const node of nodeList) {
    const nodePhotos = photosByNode.get(node.id) ?? []
    nodePhotos.forEach((photo, i) => {
      imageTasks.push({
        key: `photo:${photo.id}`,
        label: `Tree ${node.node_number}, photo ${i + 1}`,
        signedUrl: signedUrlByPath.get(photo.storage_path) ?? null,
        offlinePhotoId: photo.id,
        // The first photo doubles as the full-bleed photo cover, so it keeps
        // the default cap; every other photo prints in a card cell at most
        maxDimensionPx: imageTasks.length === 0 ? undefined : REPORT_PHOTO_MAX_PX,
        // Each photo is drawn once per document (inline, plates, appendix
        // and the schedule thumbnail are mutually exclusive), so the Buffer
        // src losing react-pdf's embed dedupe costs nothing
        output: 'bytes',
      })
    })
  }
  // Share the photo budget out evenly. The cover photo keeps its full size;
  // a big job's photos step down REPORT_PHOTO_LADDER to fit their share
  const photoBudget = REPORT_PHOTO_BUDGET_BYTES / Math.max(1, imageTasks.length)
  imageTasks.forEach((task, i) => {
    if (i > 0) task.maxBytes = photoBudget
  })
  if (signaturePath) {
    imageTasks.push({
      key: 'signature',
      label: 'Arborist signature',
      signedUrl: signatureSignedUrl,
      // Drawn signatures are transparent-background PNGs; JPEG would render
      // the transparency as black. Flatten onto white and keep PNG.
      format: 'png',
      background: '#ffffff',
    })
  }
  if (siteMapPath) {
    imageTasks.push({ key: 'site_map', label: 'Site overview map', signedUrl: siteMapSignedUrl })
  }
  if (tpzDiagramPath) {
    imageTasks.push({ key: 'tpz', label: 'TPZ diagram', signedUrl: tpzDiagramSignedUrl })
  }
  imageTasks.push(...brandImageTasks(brandPreset, logoSignedUrl, logoLightSignedUrl))

  const imageDataUrls = await resolveImagesToDataUrls(imageTasks, onImageProgress)

  // -------------------------------------------------------------------------
  // 7. Assemble node + profile structures with embedded images
  // -------------------------------------------------------------------------
  const nodes: ReportNode[] = nodeList.map((node) => {
    const nodePhotos = photosByNode.get(node.id) ?? []
    const photosWithUrls: ReportPhoto[] = nodePhotos.map((photo) => {
      const resolved = imageDataUrls.get(`photo:${photo.id}`)
      return {
        src: resolved?.bytes ?? '',
        width: resolved?.width ?? null,
        height: resolved?.height ?? null,
        caption: photo.caption,
      }
    })

    return {
      node_number: node.node_number,
      node_tag: customNodeTag(node),
      common_name: node.common_name,
      botanical_name: node.botanical_name,
      dbh_cm: node.dbh_cm,
      drf_cm: node.drf_cm,
      height_m: node.height_m,
      crown_spread_m: node.crown_spread_m,
      condition_rating: node.condition_rating,
      risk_rating: node.risk_rating,
      hazard_level: node.hazard_level,
      health_rating: node.health_rating,
      health_notes: node.health_notes,
      risk_assessment: node.risk_assessment,
      recommended_action: node.recommended_action,
      notes: node.notes,
      invasive_status: (node as Record<string, unknown>).invasive_status as string | null,
      photos: photosWithUrls,
    }
  })

  const arboristProfile: ReportArboristProfile | null = profileRow
    ? {
        company_name: profileRow.company_name,
        abn: profileRow.abn,
        qualifications: profileRow.qualifications,
        certifications: profileRow.certifications,
        memberships: profileRow.memberships,
        licence_numbers: profileRow.licence_numbers,
        signatureUrl: imageDataUrls.get('signature')?.dataUrl ?? null,
      }
    : null

  // -------------------------------------------------------------------------
  // 8. Extract narrative sections and toggles from reportData
  // -------------------------------------------------------------------------
  const rd = reportData ?? {}

  const sections: ReportSections = {
    executive_summary: typeof rd['executive_summary'] === 'string' ? rd['executive_summary'] : null,
    scope: typeof rd['scope'] === 'string' ? rd['scope'] : null,
    methodology: typeof rd['methodology'] === 'string' ? rd['methodology'] : null,
    vta_description: typeof rd['vta_description'] === 'string' ? rd['vta_description'] : null,
    health_vigor: typeof rd['health_vigor'] === 'string' ? rd['health_vigor'] : null,
    observations: typeof rd['observations'] === 'string' ? rd['observations'] : null,
    tpp_text: typeof rd['tpp_text'] === 'string' ? rd['tpp_text'] : null,
    terms_conditions: typeof rd['terms_conditions'] === 'string' ? rd['terms_conditions'] : null,
    references: typeof rd['references'] === 'string' ? rd['references'] : null,
    conclusions: typeof rd['conclusions'] === 'string' ? rd['conclusions'] : null,
    recommendations: typeof rd['recommendations'] === 'string' ? rd['recommendations'] : null,
    additional_notes: typeof rd['additional_notes'] === 'string' ? rd['additional_notes'] : null,
  }

  const bool = (key: string, fallback: boolean): boolean => {
    const v = rd[key]
    return typeof v === 'boolean' ? v : fallback
  }

  const toggles: ReportToggles = {
    include_scope: bool('include_scope', true),
    include_methodology: bool('include_methodology', true),
    include_vta: bool('include_vta', true),
    include_traq: bool('include_traq', true),
    include_document_control: bool('include_document_control', true),
    include_health_vigor: bool('include_health_vigor', true),
    include_observations: bool('include_observations', true),
    include_tpp: bool('include_tpp', false),
    include_terms: bool('include_terms', true),
    include_references: bool('include_references', true),
    include_additional_notes: bool('include_additional_notes', true),
    // Defaults on: an auto-generated marker map exists for any job with
    // GPS-located nodes, so reports carry a site map unless opted out
    include_site_map: bool('include_site_map', true),
    include_tpz_diagram: bool('include_tpz_diagram', false),
    include_tree_schedule: bool('include_tree_schedule', true),
    include_tree_photos: bool('include_tree_photos', false),
  }

  // Purpose prunes sections deterministically on top of the user's toggles
  const finalToggles = brandLayout ? applyPurposeToToggles(toggles, brandLayout.purpose) : toggles

  const brand =
    brandPreset && brandLayout
      ? buildReportBrand({
          preset: brandPreset,
          layout: brandLayout,
          logoDataUrl: imageDataUrls.get('brand_logo')?.dataUrl ?? null,
          logoLightDataUrl: imageDataUrls.get('brand_logo_light')?.dataUrl ?? null,
          profile: profileRow,
        })
      : null

  // -------------------------------------------------------------------------
  // 8b. Document control (full report): distribution list + revision register
  // -------------------------------------------------------------------------
  const company = profileRow?.company_name ?? brand?.orgName ?? ''
  const documentControl =
    variant === 'full_report' && finalToggles.include_document_control
      ? await loadDocumentControl({
          jobId,
          version,
          variant,
          company,
          client: clientOrg,
          arborist: arboristUser,
        })
      : { distribution: [], revisions: [] }

  // -------------------------------------------------------------------------
  // 8c. Section order (010, full report): report → preset → built-in default.
  //     The preset's order is read from the preset itself, never the
  //     effective layout, so a layout override still follows it (R3).
  // -------------------------------------------------------------------------
  const sectionOrder =
    variant === 'full_report'
      ? effectiveSectionOrder({
          reportOrder: rdEarly['section_order'],
          presetOrder: brandPreset ? parseBrandLayout(brandPreset.layout).section_order : null,
          layout: brandLayout ?? DEFAULT_LAYOUT,
        })
      : undefined

  // -------------------------------------------------------------------------
  // 9. Return assembled props
  // -------------------------------------------------------------------------
  return {
    job: {
      title: job.title,
      job_number: job.job_number,
      description: job.description,
      scheduled_date: job.scheduled_date,
      site_address_line1: job.site_address_line1,
      site_address_city: job.site_address_city,
      site_address_state: job.site_address_state,
      site_address_postal_code: job.site_address_postal_code,
      status: job.status,
    },
    client: {
      name: clientOrg.name,
    },
    arborist: arboristUser
      ? { first_name: arboristUser.first_name, last_name: arboristUser.last_name }
      : null,
    assessor: assessorUser
      ? { first_name: assessorUser.first_name, last_name: assessorUser.last_name }
      : null,
    arboristProfile,
    nodes,
    version,
    generatedAt: new Date().toISOString(),
    summary: reportSummary,
    overallRecommendations: reportOverallRecommendations,
    sections,
    toggles: finalToggles,
    variant,
    siteMapUrl: imageDataUrls.get('site_map')?.dataUrl ?? autoSiteMapDataUrl,
    siteMapAuto: !siteMapPath && autoSiteMapDataUrl !== null,
    mapBook,
    tpzDiagramUrl: imageDataUrls.get('tpz')?.dataUrl ?? null,
    brand,
    distribution: documentControl.distribution,
    revisions: documentControl.revisions,
    ...(sectionOrder ? { sectionOrder } : {}),
  }
}

const fullName = (u: { first_name: string | null; last_name: string | null } | null | undefined) =>
  u ? [u.first_name, u.last_name].filter(Boolean).join(' ') : ''

type UserName = { first_name: string | null; last_name: string | null }

/**
 * Names for the given users via team_member_names (the caller, teammates, and
 * anyone who worked a job of the caller's teams). Best-effort: offline or on
 * error it returns what it has, and a missing name prints blank.
 */
export async function fetchUserNames(userIds: string[]): Promise<Map<string, UserName>> {
  const names = new Map<string, UserName>()
  const ids = [...new Set(userIds.filter(Boolean))]
  if (ids.length === 0) return names
  try {
    const { data, error } = await supabase.rpc('team_member_names', { p_user_ids: ids })
    if (error) throw error
    for (const u of data ?? []) {
      names.set(u.id, { first_name: u.first_name, last_name: u.last_name })
    }
  } catch (err) {
    console.warn('Could not load user names for the report:', err)
  }
  return names
}

/**
 * The full report's Distribution List and Revision Register, filled from
 * data the job already holds. Distribution: the client contact and the
 * project arborist. Revisions: every earlier version of this report type
 * that was generated, then the one being generated now (by whoever is
 * signed in, dated now). Best-effort: if the history can't be read
 * (offline), the register carries the current version alone.
 */
export async function loadDocumentControl(opts: {
  jobId: string
  version: number
  variant: ReportType
  company: string
  client: { name: string; contact_name: string | null }
  arborist: { first_name: string | null; last_name: string | null } | null
}): Promise<{ distribution: ReportDistributionEntry[]; revisions: ReportRevisionEntry[] }> {
  const distribution: ReportDistributionEntry[] = [
    { person: opts.client.contact_name ?? '', company: opts.client.name, format: 'Digital Copy' },
  ]
  const arboristName = fullName(opts.arborist)
  if (arboristName) {
    distribution.push({ person: arboristName, company: opts.company, format: 'Digital Copy' })
  }

  let currentUserId: string | null = null
  let earlier: { version: number; generated_at: string | null; generated_by: string }[] = []
  let names = new Map<string, UserName>()
  try {
    // The session is local (no network), so the current row works offline
    const {
      data: { session },
    } = await supabase.auth.getSession()
    currentUserId = session?.user.id ?? null

    const { data, error } = await supabase
      .from('reports')
      .select('version, generated_at, generated_by')
      .eq('job_id', opts.jobId)
      // reports is unique on (job_id, report_type, version): filter the type
      .eq('report_type', opts.variant)
      .lt('version', opts.version)
      .not('generated_at', 'is', null)
      .order('version', { ascending: true })
    if (error) throw error
    earlier = data ?? []

    names = await fetchUserNames(
      [...earlier.map((r) => r.generated_by), currentUserId].filter(Boolean) as string[]
    )
  } catch {
    earlier = []
  }

  const revisions: ReportRevisionEntry[] = [
    ...earlier.map((r) => ({
      revision: r.version,
      revisedBy: fullName(names.get(r.generated_by)),
      company: opts.company,
      date: r.generated_at,
    })),
    {
      revision: opts.version,
      revisedBy: (currentUserId ? fullName(names.get(currentUserId)) : '') || arboristName,
      company: opts.company,
      date: new Date().toISOString(),
    },
  ]

  return { distribution, revisions }
}
